import { useMemo } from "react";
import classNames from "classnames";
import { useRouter } from "next/router";

import AscAdminPagination from "../AscAdminPagination/AscAdminPagination";
import AscHeading from "../AscTypography/AscHeading/AscHeading";
import AscLoading from "../AscLoading/AscLoading";
import styles from "./AscScrollableTable.module.scss";
import { ClassicChevronDownIcon } from "../AscIcons";

export interface AscScrollableTableProps<T extends string> {
  className?: string;
  title?: string;
  loading?: boolean;
  columns: {
    name?: string;
    selector: T | "action";
    sortable?: boolean;
    width?: string;
  }[];
  data: (Record<T, React.ReactNode> &
    Partial<Record<"action", React.ReactNode>>)[];
  pageCount?: number;
}

export default function AscScrollableTable<T extends string>({
  className,
  columns,
  data,
  title,
  pageCount = 10,
  loading = false,
}: AscScrollableTableProps<T>) {
  const router = useRouter();
  const sortBy = router.query.sortBy as string;
  const sortOrder = router.query.sortOrder as string;

  const cells = useMemo(() => {
    const cells: {
      row: number;
      column: number;
      columnSelector: string;
      content: React.ReactNode;
    }[] = [];
    if (data && Array.isArray(data) && data?.length > 0) {
      data?.forEach((row, rowIndex) => {
        columns.forEach((column, columnIndex) => {
          cells.push({
            row: rowIndex,
            column: columnIndex,
            columnSelector: column?.selector,
            content: row?.[column?.selector],
          });
        });
      });
    }
    return cells;
  }, [data, columns]);

  const tableProperties = useMemo(
    () =>
      ({
        "--row-count": data?.length + 1,
        "--custom-columns": columns
          ?.map((column) => column?.width || "auto")
          ?.join(" "),
      } as React.CSSProperties),
    [data, columns]
  );

  function handleSortChange(
    column: AscScrollableTableProps<T>["columns"][number]
  ) {
    if (column?.sortable) {
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            page: 1,
            sortBy: column?.selector,
            sortOrder: sortOrder === "asc" ? "desc" : "asc",
          },
        },
        undefined,
        { shallow: true }
      );
    }
  }

  return (
    <div
      data-testid="asc-scrollable-table"
      className={classNames(styles.ground, className)}
    >
      {title ? <AscHeading level={5}>{title}</AscHeading> : null}
      {loading ? (
        <AscLoading
          className={styles.loader}
          loading={loading}
          type="spinner"
        />
      ) : (
        <>
          <div className={classNames(styles.table)} style={tableProperties}>
            {columns?.map((column) => (
              <div
                key={column.selector}
                className={classNames(styles.headerCell)}
              >
                {column.name}
                {column?.sortable ? (
                  <ClassicChevronDownIcon
                    className={classNames(styles.sortIcon, {
                      [styles.active]: sortBy === column?.selector,
                      [styles.asc]: sortOrder === "asc",
                      [styles.desc]: sortOrder === "desc",
                    })}
                    onClick={() => handleSortChange(column)}
                  />
                ) : null}
              </div>
            ))}
            {cells?.map((cell) => (
              <div
                data-row={cell?.row}
                data-column={cell?.column}
                key={`${cell?.column}-${cell?.row}`}
                className={classNames(styles.cell)}
              >
                {cell?.content}
              </div>
            ))}
          </div>
          <footer className={styles.footer}>
            <AscAdminPagination pageCount={pageCount} />
          </footer>
        </>
      )}
    </div>
  );
}
